import { memo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { TypographyVariant } from '../../utils/theme';
import { slugify } from '../../utils/slugify';
import Tooltip from '../mantine-tooltip';
import type { RIconComponent } from '../remix-icon';
import RIcon from '../remix-icon';
import Typography from '../typography/typography';

interface PopoverActionProps<T> {
  data?: T;
  ariaLabel?: string;
  label: string;
  description?: string;
  icon?: RIconComponent;
  handleClick: (data?: T) => void;
  isDisabled?: boolean;
  disabledTooltip?: string;
}

const PopoverActionItem = <T,>({
  ariaLabel,
  label,
  description,
  handleClick,
  data,
  icon,
  isDisabled,
  disabledTooltip,
}: PopoverActionProps<T>) => {
  const theme = useTheme();
  return (
    <Tooltip label={isDisabled ? disabledTooltip : undefined} position="bottom">
      <Wrapper
        onClick={() => {
          if (!isDisabled) handleClick(data);
        }}
        role="button"
        aria-label={ariaLabel ?? label}
        aria-disabled={isDisabled}
        tabIndex={0}
        onKeyDown={(event) => {
          if (!isDisabled && (event.key === 'Enter' || event.key === ' ')) {
            handleClick(data);
          }
        }}
        data-testid={slugify(label)}
        $isDisabled={isDisabled}
        $hasIcon={!!icon}
      >
        {icon && (
          <IconContainer>
            <RIcon icon={icon} color={theme.palette.primary.main} size="lg" />
          </IconContainer>
        )}

        <TextContainer>
          <Typography variant={TypographyVariant.bodyMd1}>{label}</Typography>
          {description && (
            <Typography
              variant={TypographyVariant.bodySm1}
              color={theme.palette.neutral['90']}
            >
              {description}
            </Typography>
          )}
        </TextContainer>
      </Wrapper>
    </Tooltip>
  );
};

const Wrapper = styled.div<{ $isDisabled?: boolean; $hasIcon?: boolean }>`
  ${({ theme, $isDisabled, $hasIcon }) => css`
    cursor: ${$isDisabled ? 'not-allowed' : 'pointer'};
    opacity: ${$isDisabled ? 0.6 : 1};
    padding: ${theme.spacing(2.5, 2)};
    display: flex;
    gap: ${$hasIcon ? theme.spacing(2.5) : theme.spacing(0)};
    max-width: 362px;

    &:hover {
      background: ${$isDisabled ? 'transparent' : theme.palette.neutral[40]};
    }
  `}
`;

const IconContainer = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing(0.75)};
    background: ${theme.palette.primary.bg};
    border-radius: ${theme.borderRadius.sm};
    height: 44px;
  `}
`;

const TextContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spacing(0.5)};
    flex-direction: column;
  `}
`;

export default memo(PopoverActionItem);
